/**
 * Gestion des équipements énergétiques et de leurs dépendances
 *
 * Ce module permet de représenter les équipements énergétiques d'un bâtiment
 * sous forme de graphe dirigé acyclique, où chaque noeud représente un équipement
 * et les relations représentent les dépendances énergétiques.
 */

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Template de noeud représentant un type d'équipement possible
 */
export type NodeTemplate = {
    idname: string
    display_name: string
    idname_inputs?: string[]
    params?: {
        is_present?: {
            guessed: boolean
            validated: boolean
        }
        idsource?: {
            guessed: number
            validated: number
        }
        is_linky?: {
            guessed: boolean
            validated: boolean
        }
        number?: number | null
    }
}

/**
 * Noeud du graphe représentant un équipement dans une instance
 */
export type EnergyUseNode = {
    idname: string
    display_name: string
    idname_inputs?: string[]
    params?: {
        is_present?: {
            guessed: boolean
            validated: boolean
        }
        idsource?: {
            guessed: number
            validated: number
        }
        is_linky?: {
            guessed: boolean
            validated: boolean
        }
        number?: number | null
    }
    _id: string
    _id_input?: string
}

/**
 * Formulaire pour créer un graphe
 */
export type EnergyUseForm = {
    chauffage: "elec_radiateur" | "elec_pac" | "gaz" | "fioul" | null
    ecs: "elec_ballon" | "gaz" | "fioul" | null
    equipements_elec: string[]
    radiateurs: {
        elec: number
        eau: number
    }
}

/**
 * Templates de noeuds disponibles dans le système
 */
const NODES_TEMPLATES: NodeTemplate[] = [
    {
        idname: "elec",
        display_name: "Compteur électrique",
        params: {
            is_present: {
                guessed: true,
                validated: false,
            },
            idsource: {
                guessed: 123456,
                validated: 123456,
            },
            is_linky: {
                guessed: true,
                validated: false,
            },
        },
    },
    {
        idname: "gaz",
        display_name: "Compteur de gaz",
        // Parents possibles : ["chaudiere_gaz"]
    },
    {
        idname: "chaudiere_fioul",
        display_name: "Chaudière au fioul",
        idname_inputs: ["elec"],
        // Parents possibles : ["radiateur_eau", "ecs"]
    },
    {
        idname: "chaudiere_gaz",
        display_name: "Chaudière au gaz",
        idname_inputs: ["gaz"],
        // Parents possibles : ["ecs", "radiateur_eau"]
    },
    {
        idname: "pac",
        display_name: "Pompe à chaleur",
        idname_inputs: ["elec"],
        // Parents possibles : ["radiateur_eau"]
    },
    {
        idname: "ballon_eau",
        display_name: "Ballon d'eau chaude",
        idname_inputs: ["elec"],
        // Parents possibles : ["ecs"]
    },
    {
        display_name: "Eau chaude sanitaire",
        idname: "ecs",
        idname_inputs: ["ballon_eau", "chaudiere_gaz", "chaudiere_fioul"],
        // Parents possibles : []
    },
    {
        display_name: "Chauffage",
        idname: "chauffage",
        idname_inputs: ["radiateur_elec", "radiateur_eau"],
        // Parents possibles : []
    },
    {
        display_name: "Radiateur électrique",
        idname: "radiateur_elec",
        idname_inputs: ["elec"],
        params: {
            number: null,
        },
        // Parents possibles : ["chauffage"]
    },
    {
        display_name: "Radiateur à Eau",
        idname: "radiateur_eau",
        idname_inputs: ["chaudiere_fioul", "chaudiere_gaz", "pac"],
        params: {
            number: null,
        },
        // Parents possibles : ["chauffage"]
    },
    {
        display_name: "Lampe",
        idname: "lampe",
        idname_inputs: ["elec"],
        params: {
            number: null,
        },
        // Parents possibles : []
    },
    {
        display_name: "Eclairage",
        idname: "eclairage",
        idname_inputs: ["lampe"],
        // Parents possibles : []
    },
    {
        display_name: "Piscine",
        idname: "piscine",
        idname_inputs: ["elec"],
        // Parents possibles : []
    },
    {
        display_name: "Sauna",
        idname: "sauna",
        idname_inputs: ["elec"],
        // Parents possibles : []
    },
    {
        display_name: "Voiture",
        idname: "voiture",
        idname_inputs: ["elec"],
        // Parents possibles : []
    },
]

/**
 * Classe représentant les différents équipements et leurs dépendances énergétiques.
 *
 * On représente ces dépendances sous forme de graphe dirigé acyclique, en stockant dans chaque noeud
 * son/ses successeurs dans le graphe.
 *
 * Par construction, on liste dans le noeud ses successeurs possibles mais dans une instance de graphe
 * seul un successeur est possible.
 * La relation des graphes se lit parent -> enfant. Le champ "idname_inputs" représente les enfants
 * possibles de chaque noeud, mais dans une instance de graphe on ne peut pas avoir plus d'un enfant
 * (l'_id de cet enfant est stocké dans le champ "_id_input").
 *
 * Chaque noeud peut avoir une multiplicité quelconque entrante (i.e. plusieurs parents). Les noeuds
 * du template peuvent avoir une multiplicité sortante multiple (i.e. plusieurs enfants possibles)
 * mais les noeuds d'une instance de graphe ne peuvent avoir qu'une multiplicité sortante égale à 1
 * pour les équipements qui se branchent sur un autre équipement ou 0 pour le cas d'un compteur
 * (i.e. au plus un enfant).
 */
export class EnergyUse {
    /**
     * Templates de noeuds disponibles
     */
    private nodesTemplates: NodeTemplate[]

    /**
     * Noeuds du graphe
     */
    private nodes: EnergyUseNode[]

    /**
     * Initialise une instance de EnergyUse
     * @param nodes - Liste optionnelle de noeuds à importer dans le graphe
     */
    constructor(nodes: EnergyUseNode[] = []) {
        this.nodesTemplates = NODES_TEMPLATES
        this.nodes = nodes
    }

    /**
     * Génère un identifiant unique pour un noeud
     * @returns Identifiant unique sous forme de chaîne de caractères
     */
    private generateId(): string {
        // Génère un UUID v4 et prend les 8 premiers caractères
        return crypto.randomUUID().split("-")[0]
    }

    /**
     * Récupère un template de noeud par son idname
     * @param idname - Identifiant du template à récupérer
     * @returns Le template de noeud correspondant
     * @throws {Error} Si le template n'existe pas
     */
    private getNodeTemplateByIdname(idname: string): NodeTemplate {
        const node = this.nodesTemplates.find((n) => n.idname === idname)
        if (!node) {
            throw new Error(`Le template du noeud ${idname} n'existe pas`)
        }
        return node
    }

    /**
     * Récupère un noeud par son _id
     * @param _id - Identifiant du noeud à récupérer
     * @returns Le noeud correspondant
     * @throws {Error} Si le noeud n'existe pas
     */
    getNodeById(_id: string): EnergyUseNode {
        const node = this.nodes.find((n) => n._id === _id)
        if (!node) {
            throw new Error("Ce noeud n'existe pas")
        }
        return node
    }

    /**
     * Récupère un noeud par son idname
     * @param idname - Identifiant du noeud à récupérer
     * @returns Le premier noeud trouvé avec cet idname, ou null si aucun n'est trouvé
     */
    getNodeByIdname(idname: string): EnergyUseNode | null {
        const node = this.nodes.find((n) => n.idname === idname)
        return node || null
    }

    /**
     * Ajoute un noeud dans le graphe
     *
     * Si le noeud n'a pas d'enfant (_id_node_enfant à undefined), on ajoute le noeud en l'état.
     * Si le noeud a un enfant, on vérifie que l'enfant existe et on ajoute le lien au parent
     * (on ajoute dans le champ '_id_input' du noeud parent l'"_id" du noeud enfant).
     *
     * Si le noeud ajouté n'a qu'un parent possible, on l'ajoute aussi automatiquement.
     *
     * @param nodeTemplate - Template du noeud à ajouter
     * @param idNodeEnfant - Identifiant du noeud enfant (optionnel)
     * @param params - Paramètres du noeud (optionnel)
     * @returns Le noeud ajouté avec son _id et ses paramètres
     * @throws {Error} Si le noeud doit avoir un enfant mais aucun n'a été fourni
     * @throws {Error} Si le lien parent -> enfant n'existe pas dans le template
     */
    addNode(
        nodeTemplate: NodeTemplate,
        idNodeEnfant?: string,
        params: EnergyUseNode["params"] = {}
    ): EnergyUseNode {
        // Génération d'un _id aléatoire
        const _id = this.generateId()
        const node: EnergyUseNode = {
            ...nodeTemplate,
            _id,
            params: params || nodeTemplate.params,
        }

        // Si le noeud n'a pas d'enfant possible
        if (!node.idname_inputs || node.idname_inputs.length === 0) {
            // Ajout du noeud au graphe
            this.nodes.push(node)
        } else {
            // Si le noeud a un parent
            if (idNodeEnfant === undefined) {
                throw new Error(
                    "Le noeud à ajouter doit avoir un enfant, aucun enfant n'a été fourni"
                )
            }

            // On récupère le noeud enfant
            const nodeEnfant = this.getNodeById(idNodeEnfant)

            // On vérifie que les noeuds parents / enfants sont compatibles
            if (!node.idname_inputs.includes(nodeEnfant.idname)) {
                throw new Error(
                    `Le lien ${node.idname} -> ${nodeEnfant.idname} n'existe pas`
                )
            }

            // Ajout du lien
            node._id_input = nodeEnfant._id

            // Ajout du noeud
            this.nodes.push(node)

            // Ajout automatique du noeud parent s'il n'y a qu'une et une seule possibilité
            // On parcourt tous les noeuds qui peuvent exister dans le graphe, et on sélectionne
            // tous les noeuds qui peuvent avoir comme enfant le noeud que l'on souhaite ajouter.
            const parentsNodesTemplates: NodeTemplate[] = []
            for (const template of this.nodesTemplates) {
                // Check l'idname du noeud que l'on considère est dans les enfants possibles du noeud
                if (
                    template.idname_inputs &&
                    template.idname_inputs.includes(node.idname)
                ) {
                    parentsNodesTemplates.push(template)
                }
            }
            if (parentsNodesTemplates.length === 1) {
                // S'il n'y a qu'un parent possible on ajoute récursivement ce parent
                return this.addNode(parentsNodesTemplates[0], node._id)
            }
        }

        return node
    }

    /**
     * Supprime un noeud et les parents qui seront laissés sans enfant dans le graphe
     *
     * Les noeuds sans enfant devant être uniquement des compteurs de gaz ou d'élec
     * (i.e. des sources d'énergie), on supprime récursivement tous les parents
     * qui se retrouvent sans enfant.
     *
     * @param idNode - Identifiant du noeud à supprimer
     * @returns Le noeud supprimé
     */
    deleteNode(idNode: string): EnergyUseNode {
        const nodeToDelete = this.getNodeById(idNode)
        // Suppression du noeud
        this.nodes = this.nodes.filter((node) => node._id !== nodeToDelete._id)
        // Suppression des parents sans enfants
        for (const node of this.nodes) {
            // Check si le noeud supprimé est dans l'enfant d'un autre noeud
            if (nodeToDelete._id === node._id_input) {
                return this.deleteNode(node._id)
            }
        }

        return nodeToDelete
    }

    /**
     * Renvoie la liste de tous les noeuds qui peuvent être ajoutés comme parent du noeud
     * @param idNode - Identifiant du noeud (optionnel, si non fourni liste les sources)
     * @returns Liste des idname des parents possibles
     */
    listPossibleParentNodes(idNode?: string): string[] {
        const possibleParents: string[] = []

        // si idNode est vide, on liste les sources (les noeuds du graphe qui ne peuvent pas avoir d'enfant)
        if (idNode === undefined) {
            for (const nodeTemplate of this.nodesTemplates) {
                if (
                    !nodeTemplate.idname_inputs ||
                    nodeTemplate.idname_inputs.length === 0
                ) {
                    possibleParents.push(nodeTemplate.idname)
                }
            }
        } else {
            // sinon, on liste les parents du noeud donné
            const node = this.getNodeById(idNode)
            for (const nodeTemplate of this.nodesTemplates) {
                // Si le noeud node est dans les enfants possibles de nodeTemplate, on ajoute nodeTemplate à la liste des parents possibles
                if (
                    nodeTemplate.idname_inputs &&
                    nodeTemplate.idname_inputs.includes(node.idname)
                ) {
                    possibleParents.push(nodeTemplate.idname)
                }
            }
        }
        return possibleParents
    }

    /**
     * Fonction récursive qui imprime le prochain niveau dans le graphe
     * @param nodeCurrent - Noeud actuel à imprimer
     * @param ind - Niveau d'indentation
     * @returns Représentation textuelle du sous-graphe
     */
    private printNext(nodeCurrent: EnergyUseNode, ind: number): string {
        let out = ""
        if (ind > 0) {
            out += "\n" + "  ".repeat(ind) + "!--> "
        } else {
            out += "\n"
        }
        out += `${nodeCurrent.idname} (${nodeCurrent._id}) ${JSON.stringify(nodeCurrent.params || "")}`
        // Recherche des enfants
        for (const node of this.nodes) {
            if (nodeCurrent._id_input && node._id === nodeCurrent._id_input) {
                out += this.printNext(node, ind + 1)
            }
        }
        return out
    }

    /**
     * Représentation textuelle du graphe
     * @returns Représentation textuelle du graphe
     */
    toString(): string {
        const out: string[] = []
        for (const node of this.nodes) {
            if (this.listPossibleParentNodes(node._id).length === 0) {
                out.push(this.printNext(node, 0))
            }
        }
        return out.join("\t\t")
    }

    /**
     * Création du graphe à partir d'un formulaire
     *
     * @param form - Formulaire contenant les informations sur les équipements
     * @example
     * ```typescript
     * const form = {
     *     chauffage: "elec_radiateur",
     *     ecs: "elec_ballon",
     *     equipements_elec: ["piscine", "sauna", "voiture"],
     *     radiateurs: {
     *         elec: 2,
     *         eau: 3
     *     }
     * }
     * ```
     */
    createGraphFromForm(form: EnergyUseForm): void {
        // On purge le graphique
        this.nodes = []

        // Ajout d'un compteur électrique
        const compteurElecTemplate = this.getNodeTemplateByIdname("elec")
        const compteurElec1 = this.addNode(compteurElecTemplate)

        // Si le chauffage ou l'ecs est au gaz on ajoute un compteur de gaz
        let compteurGaz1: EnergyUseNode | null = null
        if (form.chauffage === "gaz" || form.ecs === "gaz") {
            const compteurGazTemplate = this.getNodeTemplateByIdname("gaz")
            compteurGaz1 = this.addNode(compteurGazTemplate)
        }

        // Gestion du chauffage
        if (form.chauffage === "elec_radiateur") {
            const radiateur1 = this.getNodeTemplateByIdname("radiateur_elec")
            const params = {
                number: form.radiateurs?.elec || 0,
            }
            this.addNode(radiateur1, compteurElec1._id, params)
        } else if (form.chauffage === "elec_pac") {
            const pac1 = this.getNodeTemplateByIdname("pac")
            this.addNode(pac1, compteurElec1._id)
            // ajout du nombre de radiateurs
            const radiateurNode = this.getNodeByIdname("radiateur_eau")
            if (radiateurNode) {
                radiateurNode.params = {
                    ...radiateurNode.params,
                    number: form.radiateurs?.eau || 0,
                }
            }
        } else if (form.chauffage === "gaz") {
            if (!compteurGaz1) {
                const compteurGazTemplate = this.getNodeTemplateByIdname("gaz")
                compteurGaz1 = this.addNode(compteurGazTemplate)
            }
            const chaudiereGazTemplate =
                this.getNodeTemplateByIdname("chaudiere_gaz")
            const chaudiereGaz1 = this.addNode(
                chaudiereGazTemplate,
                compteurGaz1._id
            )
            // Ajout des équipements
            const radiateur1 = this.getNodeTemplateByIdname("radiateur_eau")
            const params = {
                number: form.radiateurs?.eau || 0,
            }
            this.addNode(radiateur1, chaudiereGaz1._id, params)
        } else if (form.chauffage === "fioul") {
            const chaudiereFioulTemplate =
                this.getNodeTemplateByIdname("chaudiere_fioul")
            const chaudiereFioul1 = this.addNode(
                chaudiereFioulTemplate,
                compteurElec1._id
            )
            // Ajout des équipements
            const radiateur1 = this.getNodeTemplateByIdname("radiateur_eau")
            const params = {
                number: form.radiateurs?.eau || 0,
            }
            this.addNode(radiateur1, chaudiereFioul1._id, params)
        }

        // Gestion de l'ECS
        if (form.ecs === "elec_ballon") {
            const ballonEau1 = this.getNodeTemplateByIdname("ballon_eau")
            this.addNode(ballonEau1, compteurElec1._id)
        } else if (form.ecs === "fioul") {
            // On vérifie si une chaudière au fioul n'a pas déjà été installée
            let chaudiereFioul1 = this.getNodeByIdname("chaudiere_fioul")
            if (chaudiereFioul1 === null) {
                const chaudiereFioulTemplate =
                    this.getNodeTemplateByIdname("chaudiere_fioul")
                chaudiereFioul1 = this.addNode(
                    chaudiereFioulTemplate,
                    compteurElec1._id
                )
            }
            // Ajout de l'usage
            const ecs1 = this.getNodeTemplateByIdname("ecs")
            this.addNode(ecs1, chaudiereFioul1._id)
        } else if (form.ecs === "gaz") {
            if (!compteurGaz1) {
                const compteurGazTemplate = this.getNodeTemplateByIdname("gaz")
                compteurGaz1 = this.addNode(compteurGazTemplate)
            }
            // On vérifie si une chaudière au gaz n'a pas déjà été installée
            let chaudiereGaz1 = this.getNodeByIdname("chaudiere_gaz")
            if (chaudiereGaz1 === null) {
                const chaudiereGazTemplate =
                    this.getNodeTemplateByIdname("chaudiere_gaz")
                chaudiereGaz1 = this.addNode(
                    chaudiereGazTemplate,
                    compteurGaz1._id
                )
            }
            // Ajout de l'usage
            const ecs1 = this.getNodeTemplateByIdname("ecs")
            this.addNode(ecs1, chaudiereGaz1._id)
        }

        // Ajout des autres équipements
        for (const u of form.equipements_elec || []) {
            if (u === "piscine") {
                const piscine = this.getNodeTemplateByIdname("piscine")
                this.addNode(piscine, compteurElec1._id)
            } else if (u === "sauna") {
                const sauna = this.getNodeTemplateByIdname("sauna")
                this.addNode(sauna, compteurElec1._id)
            } else if (u === "voiture") {
                const voiture = this.getNodeTemplateByIdname("voiture")
                this.addNode(voiture, compteurElec1._id)
            }
        }
    }

    /**
     * Crée un formulaire à partir du graphe actuel
     * @returns Le formulaire correspondant au graphe
     */
    createFormFromGraph(): EnergyUseForm {
        const out: EnergyUseForm = {
            chauffage: null,
            ecs: null,
            radiateurs: {
                elec: 0,
                eau: 0,
            },
            equipements_elec: [],
        }

        for (const node of this.nodes) {
            if (node.idname === "radiateur_elec") {
                out.chauffage = "elec_radiateur"
                out.radiateurs.elec = (node.params?.number as number) || 0
            } else if (node.idname === "radiateur_eau") {
                out.radiateurs.eau = (node.params?.number as number) || 0
                // Recherche de l'enfant pour connaître la source d'énergie
                if (node._id_input) {
                    const enfant = this.getNodeById(node._id_input)
                    if (enfant.idname === "chaudiere_gaz") {
                        out.chauffage = "gaz"
                    } else if (enfant.idname === "chaudiere_fioul") {
                        out.chauffage = "fioul"
                    } else if (enfant.idname === "pac") {
                        out.chauffage = "elec_pac"
                    }
                }
            } else if (node.idname === "ecs") {
                // Recherche de l'enfant pour connaître la source d'énergie
                if (node._id_input) {
                    const enfant = this.getNodeById(node._id_input)
                    if (enfant.idname === "chaudiere_gaz") {
                        out.ecs = "gaz"
                    } else if (enfant.idname === "chaudiere_fioul") {
                        out.ecs = "fioul"
                    } else if (enfant.idname === "ballon_eau") {
                        out.ecs = "elec_ballon"
                    }
                }
            } else if (node.idname === "sauna") {
                out.equipements_elec.push("sauna")
            } else if (node.idname === "voiture") {
                out.equipements_elec.push("voiture")
            } else if (node.idname === "piscine") {
                out.equipements_elec.push("piscine")
            }
        }

        return out
    }

    /**
     * Récupère tous les noeuds du graphe
     * @returns Liste des noeuds du graphe
     */
    getNodes(): EnergyUseNode[] {
        return this.nodes
    }

    /**
     * Définit les noeuds du graphe
     * @param nodes - Liste des noeuds à définir
     */
    setNodes(nodes: EnergyUseNode[]): void {
        this.nodes = nodes
    }
}
